import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Lock, Pencil, Check, X, Lightbulb } from "lucide-react";
import { Button, Pill, NarrativeCard, Input, EmptyState } from "@/design/components";
import { useSiteVisitQuestions } from "@/domain/hooks";
import { useSiteVisitQuestionActions } from "@/domain/useMutations";
import { MAX_CUSTOM_QUESTIONS } from "@/domain/types";
import { toast } from "sonner";

// Guidance modelled on the questions the demo shipped with — specific, tied to a
// piece of evidence, answerable on site with a photo or a note.
const GOOD_QUESTION_EXAMPLES = [
  "Lift photographs per bay",
  "Contractor structural sign-off",
  "Steel delivery notes",
  "Beneficiary register matches attendance",
];

export function SiteVisitQuestionsSettings() {
  const questions = useSiteVisitQuestions();
  const { add, update, remove } = useSiteVisitQuestionActions();
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");

  const compulsory = questions.filter((q) => q.compulsory);
  const custom = questions.filter((q) => !q.compulsory);
  const remaining = MAX_CUSTOM_QUESTIONS - custom.length;
  const atCap = remaining <= 0;

  async function addQuestion() {
    const label = draft.trim();
    if (!label) return;
    if (atCap) {
      toast.error(`You can add at most ${MAX_CUSTOM_QUESTIONS} custom questions.`);
      return;
    }
    setDraft("");
    try {
      await add(label);
      toast.success("Question added");
    } catch {
      toast.error("Could not add the question");
    }
  }

  function startEdit(id: string, label: string) {
    setEditingId(id);
    setEditLabel(label);
  }
  async function saveEdit() {
    const id = editingId;
    const label = editLabel.trim();
    setEditingId(null);
    setEditLabel("");
    if (id && label) {
      try {
        await update(id, label);
        toast.success("Question updated");
      } catch {
        toast.error("Could not update the question");
      }
    }
  }

  async function removeQuestion(id: string) {
    try {
      await remove(id);
      toast.success("Question removed");
    } catch {
      toast.error("Could not remove the question");
    }
  }

  return (
    <div className="space-y-5">
      <Link
        to="/stratus/portfolio/site-visits"
        className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
      >
        <ArrowLeft className="h-3 w-3" /> Back to site visits
      </Link>

      <div>
        <h2 className="text-lg font-semibold text-ink">Site visit questions</h2>
        <p className="mt-0.5 text-sm text-mute">
          The checklist every oversight site visit is built from. Compulsory questions always
          apply; add up to {MAX_CUSTOM_QUESTIONS} of your own. Changes apply to visits started
          from now on — visits already recorded keep the questions they were done with.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {/* Compulsory core */}
          <section className="space-y-2">
            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-faint">
              <Lock className="h-3 w-3" /> Compulsory · always on every visit
            </h3>
            {compulsory.map((q) => (
              <div key={q.id} className="flex items-center justify-between gap-3 rounded-lg border border-line bg-subtle/40 p-3">
                <p className="min-w-0 break-words text-sm font-medium text-ink">{q.label}</p>
                <span className="shrink-0"><Pill variant="neutral">Compulsory</Pill></span>
              </div>
            ))}
          </section>

          {/* Custom questions */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">
                Your questions
              </h3>
              <span className="data text-xs text-mute">{custom.length} / {MAX_CUSTOM_QUESTIONS} used</span>
            </div>

            {custom.length === 0 ? (
              <EmptyState
                title="No custom questions yet"
                body="Add questions below to tailor what oversight checks on site."
              />
            ) : (
              custom.map((q) => (
                <div key={q.id} className="flex items-center justify-between gap-3 rounded-lg border border-line bg-card p-3">
                  {editingId === q.id ? (
                    <div className="flex flex-1 items-center gap-2">
                      <Input
                        autoFocus
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit();
                          if (e.key === "Escape") { setEditingId(null); setEditLabel(""); }
                        }}
                        className="flex-1"
                      />
                      <button type="button" aria-label="Save" onClick={saveEdit} className="-my-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-ok hover:bg-subtle">
                        <Check className="h-4 w-4" />
                      </button>
                      <button type="button" aria-label="Cancel" onClick={() => { setEditingId(null); setEditLabel(""); }} className="-my-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-mute hover:bg-subtle">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="min-w-0 flex-1 break-words text-sm font-medium text-ink">{q.label}</p>
                      <div className="flex shrink-0 items-center gap-1">
                        <button type="button" aria-label="Edit" onClick={() => startEdit(q.id, q.label)} className="-my-2 flex h-11 w-11 items-center justify-center rounded-md text-mute hover:bg-subtle hover:text-ink">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Remove"
                          onClick={() => removeQuestion(q.id)}
                          className="-my-2 flex h-11 w-11 items-center justify-center rounded-md text-mute hover:bg-badSoft hover:text-badStrong"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}

            {/* Add row */}
            <div className="flex items-center gap-2 pt-1">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addQuestion(); }}
                placeholder={atCap ? `Limit of ${MAX_CUSTOM_QUESTIONS} reached` : "Add a question, e.g. “Fire hydrant pressure tested”"}
                disabled={atCap}
                className="flex-1"
              />
              <Button size="sm" onClick={addQuestion} disabled={atCap || !draft.trim()}>
                <Plus className="h-4 w-4" /> Add
              </Button>
            </div>
          </section>
        </div>

        {/* Guidance */}
        <div className="space-y-3">
          <NarrativeCard
            variant="briefing"
            eyebrow="How to write a good question"
            title="Specific, and answerable on site"
            body="A strong question names one thing an oversight visitor can verify in person against what the ledger claims — then judge as claimed, partial, not as claimed, or could not verify, with a photo or note attached. Keep each to one check; avoid vague prompts like “everything OK?”."
          />
          <div className="rounded-lg border border-line bg-card p-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-ink">
              <Lightbulb className="h-3.5 w-3.5 text-brand" /> Examples
            </p>
            <ul className="mt-2 space-y-1.5">
              {GOOD_QUESTION_EXAMPLES.map((ex) => (
                <li key={ex} className="break-words text-sm text-mute">· {ex}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
